import { array, record, nonEmptyArray, option } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { useLocalizeProperty } from '@mews/framework';
import { RateType, useRepositoryQuery } from '@mews-commander/data-access';
import { useCurrentEnterpriseId } from '@mews-commander/core';
import { createOption } from '@mews-commander/ui';

export const useGroupedByServiceRatesOptions = () => {
    const enterpriseId = useCurrentEnterpriseId();
    const lp = useLocalizeProperty();

    return useRepositoryQuery(
        'rates',
        'getAll',
        {
            enterpriseIds: [enterpriseId],
            extent: { services: true },
        },
        {
            select: ({ rateGroups, rates, services }) => {
                if (rates && array.isNonEmpty(rates) && rateGroups && services) {
                    const ratesGroupedByGroup = pipe(
                        rates,
                        array.filter((rate) => rate.type !== RateType.AvailabilityBlock),
                        nonEmptyArray.groupBy((rate) => rate.rateGroupId)
                    );

                    const groupsGroupedByService = pipe(
                        rateGroups,
                        nonEmptyArray.groupBy((group) => group.serviceId),
                        record.map(
                            array.map((group) => ({
                                ...group,
                                rates: ratesGroupedByGroup[group.id] ?? [],
                            }))
                        )
                    );

                    return pipe(
                        services,
                        array.filterMap((service) => {
                            const groups = groupsGroupedByService[service.id];

                            if (groups && array.isNonEmpty(groups)) {
                                return option.some({
                                    groupLabel: lp(service.names),
                                    groupOptions: groups.flatMap((group) =>
                                        group.rates.map((rate) =>
                                            createOption({
                                                value: rate.id,
                                                label: rate.name,
                                            })
                                        )
                                    ),
                                });
                            }
                            return option.none;
                        })
                    );
                }
                return [];
            },
        }
    );
};
